import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Badge, Button, Dropdown, Nav, Navbar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { setCredentials, logout } from '../slices/authSlice';
import { resetOrder } from '../slices/orderSlice';
import { useGetUserProfileQuery, useLogoutMutation } from '../slices/usersApiSlice';
import { useNotificationContext } from '../providers/NotificationProvider';
import { themeVariants } from '../config.js';
import { useAppContext } from '../providers/AppProvider.js';
import { useBreakpoints } from '../hooks/useBreakpoints';
import GlobalSearchBox from './GlobalSearchBox';
import Logo from './common/Logo';
import MobileNavbarPageTitle from './common/MobileNavbarPageTitle';
import { useOptionalMobileNavbarChrome } from './common/mobileNavbarChromeContext';
import NavbarChartWorkbenchButton from './navbar/top/NavbarChartWorkbenchButton';
import NavbarTopWorkMenus from './navbar/top/NavbarTopWorkMenus';
import NotificationDropdown from './NotificationDropdown';
import ThemeControlDropdown from './ThemeControlDropdown.jsx';
import SearchTourGuide from './SearchTourGuide';
import { hasPermission, PERM } from '../utils/accessPermissions';
import { getAppMainScrollElement, getAppMainScrollInnerElement, getDashboardMainScrollElement } from '../utils/appMainScroll';
import { WORKBENCH_MOBILE_LIST_SCROLL_SELECTOR } from '../utils/mobileScrollChrome';

const THEME_LABELS = {
	light: { icon: 'sun', label: '浅色' },
	dark: { icon: 'moon', label: '深色' },
	auto: { icon: 'adjust', label: '自动' },
};

const getUserInitial = (name) => {
	if (!name) return 'U';
	if (/[\u4e00-\u9fa5]/.test(name)) {
		return name.charAt(0);
	}
	return name.charAt(0).toUpperCase();
};

const adjustColorBrightness = (hex, percent) => {
	const num = parseInt(hex.replace('#', ''), 16);
	const r = Math.max(0, Math.min(255, (num >> 16) + percent));
	const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + percent));
	const b = Math.max(0, Math.min(255, (num & 0x0000ff) + percent));
	return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const hexToRgba = (hex, alpha = 0.4) => {
	const num = parseInt(hex.replace('#', ''), 16);
	const r = (num >> 16) & 255;
	const g = (num >> 8) & 255;
	const b = num & 255;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getPrimaryAvatarStyles = () => {
	const primaryColor =
		getComputedStyle(document.documentElement).getPropertyValue('--app-theme-primary').trim() ||
		getComputedStyle(document.documentElement).getPropertyValue('--falcon-primary').trim() ||
		'#e29b38';

	const lightColor = adjustColorBrightness(primaryColor, 20);
	const darkColor = adjustColorBrightness(primaryColor, -15);
	const gradient = `linear-gradient(135deg, ${lightColor} 0%, ${primaryColor} 50%, ${darkColor} 100%)`;
	const shadowColor = hexToRgba(primaryColor, 0.4);

	return {
		gradient,
		shadowColor,
	};
};

/**
 * 顶栏对齐 Falcon `navbar/top`；头像保留项目品牌渐变样式。
 */
const Header = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const { theme } = useSelector((state) => state.config);
	const { changeTheme } = useAppContext();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const [logoutApiCall] = useLogoutMutation();
	const { data: profile, refetch: refetchProfile } = useGetUserProfileQuery(undefined, { skip: !userInfo?._id });
	const { unreadNotificationsCount } = useNotificationContext();
	const searchTourGuideRef = useRef(null);
	const { breakpoints } = useBreakpoints();
	const isMobileNav = breakpoints.down('md');
	const mobileNavbarChrome = useOptionalMobileNavbarChrome();
	const showMobileWorkbenchTitle = isMobileNav && mobileNavbarChrome?.pageTitle;
	const [showDropShadow, setShowDropShadow] = useState(false);
	const isDashboardHome = pathname === '/dashboard' || pathname === '/demo/dashboard';
	/** 桌面首页顶栏搜索；手机首页改走底部岛搜索。演示页不看权限点。 */
	const showGlobalSearch =
		isDashboardHome &&
		!isMobileNav &&
		(pathname === '/demo/dashboard' || hasPermission(userInfo, PERM.MENU_DASHBOARD_SEARCH));
	const showDesktopTopNav = Boolean(userInfo) && !isMobileNav && !isDashboardHome;

	useEffect(() => {
		if (!userInfo?._id) return;
		refetchProfile();
	}, [pathname, userInfo?._id, refetchProfile]);

	useEffect(() => {
		if (!Array.isArray(profile?.access) || !userInfo) return;
		const next = profile.access;
		const prev = Array.isArray(userInfo.access) ? userInfo.access : [];
		const same = prev.length === next.length && prev.every((key, index) => key === next[index]);
		if (same && userInfo.role === profile.role) return;
		dispatch(setCredentials({ ...userInfo, role: profile.role || userInfo.role, access: next }));
	}, [profile, userInfo, dispatch]);

	/** Falcon NavbarTop：下滑后挂 navbar-glass-shadow（主题 SCSS 半透明玻璃 + 底阴影） */
	useEffect(() => {
		const collectScrollRoots = () =>
			[
				document.documentElement,
				getDashboardMainScrollElement(),
				getAppMainScrollInnerElement(),
				getAppMainScrollElement(),
				document.querySelector(WORKBENCH_MOBILE_LIST_SCROLL_SELECTOR),
				document.querySelector('.app-workbench__mrt-scroll'),
			].filter((el, index, list) => el && list.indexOf(el) === index);

		const updateDropShadow = () => {
			const scrolled = collectScrollRoots().some((el) => (el.scrollTop ?? 0) > 0);
			setShowDropShadow(scrolled);
		};

		updateDropShadow();
		window.addEventListener('scroll', updateDropShadow, { passive: true });
		const roots = collectScrollRoots();
		roots.forEach((el) => el.addEventListener('scroll', updateDropShadow, { passive: true }));

		return () => {
			window.removeEventListener('scroll', updateDropShadow);
			roots.forEach((el) => el.removeEventListener('scroll', updateDropShadow));
		};
	}, [pathname, isMobileNav]);

	const logoutHandler = useCallback(async () => {
		try {
			await logoutApiCall().unwrap();
			dispatch(resetOrder());
			dispatch(logout());
			navigate('/loginout');
		} catch {
			// 退出登录失败，静默处理
		}
	}, [logoutApiCall, dispatch, navigate]);

	const { userInitial, avatarStyles } = useMemo(() => {
		const initial = getUserInitial(userInfo?.name);
		const styles = getPrimaryAvatarStyles();
		return { userInitial: initial, avatarStyles: styles };
	}, [userInfo?.name]);

	const handleMobileThemeChange = useCallback(
		(colorMode) => {
			if (colorMode && themeVariants.includes(colorMode)) {
				changeTheme(colorMode);
			}
		},
		[changeTheme],
	);

	const renderUserAvatar = (size = 36, fontSize = '0.9375rem', toolbar = false) => (
		<div
			className={classNames('d-flex align-items-center justify-content-center rounded-circle text-white user-avatar', {
				'user-avatar--mobile-toolbar': toolbar,
			})}
			style={
				toolbar
					? {
							fontSize,
							fontWeight: '600',
							background: avatarStyles.gradient,
							color: '#ffffff',
							lineHeight: '1',
						}
					: {
							width: `${size}px`,
							height: `${size}px`,
							fontSize,
							fontWeight: '600',
							background: avatarStyles.gradient,
							color: '#ffffff',
							boxShadow: `0 2px 8px ${avatarStyles.shadowColor}, 0 0 0 2px rgba(255,255,255,0.15), inset 0 1px 3px rgba(255,255,255,0.3)`,
							lineHeight: '1',
						}
			}
		>
			{userInitial}
		</div>
	);

	const renderAccountLinks = () => (
		<>
			<Dropdown.Item className='d-flex align-items-center gap-2 px-3 py-2' as={Link} to='/profile'>
				<FontAwesomeIcon icon='user' className='text-primary' />
				<span>个人简介 &amp; 账号</span>
			</Dropdown.Item>
			<Dropdown.Divider className='my-1' />
			<Dropdown.Item className='d-flex align-items-center justify-content-between px-3 py-2' as={Link} to='/notificationlist'>
				<div className='d-flex align-items-center gap-2'>
					<FontAwesomeIcon icon='bell' className='text-warning' />
					<span>消息中心</span>
				</div>
				{unreadNotificationsCount > 0 && (
					<Badge pill bg='danger' className='fs-10'>
						{unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
					</Badge>
				)}
			</Dropdown.Item>
			<Dropdown.Divider className='my-1' />
			<Dropdown.Item onClick={logoutHandler} className='d-flex align-items-center gap-2 px-3 py-2 text-danger'>
				<FontAwesomeIcon icon='fa-solid fa-right-from-bracket' />
				<span>退出登录</span>
			</Dropdown.Item>
		</>
	);

	const renderMobileMenu = () => (
		<Dropdown align='end' className='navbar-mobile-menu'>
			<Dropdown.Toggle as={Button} variant='link' className='navbar-mobile-icon-btn navbar-mobile-menu-toggle navbar-mobile-avatar-toggle' aria-label='用户菜单'>
				{renderUserAvatar(32, '0.875rem', true)}
			</Dropdown.Toggle>
			<Dropdown.Menu className='dropdown-menu-card dropdown-menu-end mt-2 navbar-mobile-menu-panel'>
				<div className='bg-white rounded-2 py-2 dark__bg-1000 shadow-lg border-0'>
					<div className='px-3 py-2 border-bottom mb-2'>
						<div className='d-flex align-items-center gap-2'>
							{renderUserAvatar(36, '0.9375rem')}
							<div className='flex-grow-1 min-w-0'>
								<div className='fw-semibold fs-9 text-900 text-truncate' title={userInfo?.name}>
									{userInfo?.name}
								</div>
								<div className='text-muted fs-10 text-truncate' title={userInfo?.email || userInfo?.role}>
									{userInfo?.email || userInfo?.role}
								</div>
							</div>
						</div>
					</div>
					<Dropdown.Header className='fs-10 text-600 px-3 py-1'>主题</Dropdown.Header>
					{themeVariants.map((colorMode) => {
						const config = THEME_LABELS[colorMode];
						const isActive = theme === colorMode;
						return (
							<Dropdown.Item
								key={colorMode}
								as='button'
								type='button'
								onClick={() => handleMobileThemeChange(colorMode)}
								className={classNames('d-flex align-items-center gap-2 px-3 py-2 text-900', {
									'bg-body-tertiary fw-semibold': isActive,
								})}
							>
								<FontAwesomeIcon icon={config.icon} className={isActive ? 'text-primary' : 'text-600'} />
								<span className='me-auto text-900'>{config.label}</span>
								{isActive && <FontAwesomeIcon icon='check' className='text-primary' />}
							</Dropdown.Item>
						);
					})}
					<Dropdown.Divider className='my-1' />
					{renderAccountLinks()}
				</div>
			</Dropdown.Menu>
		</Dropdown>
	);

	return (
		<>
			<SearchTourGuide ref={searchTourGuideRef} />
			<Navbar
				id='navbar'
				expand='lg'
				className={classNames('navbar-glass fs-10 navbar-top sticky-kit', {
					'navbar-glass-shadow': showDropShadow,
					'navbar-top--mobile-compact': isMobileNav,
				})}
			>
				{showMobileWorkbenchTitle ? (
					<MobileNavbarPageTitle pageTitle={mobileNavbarChrome.pageTitle} />
				) : (
					<Logo at='navbar-top' textClass='text-primary' id='topLogo' />
				)}

				{isMobileNav ? (
					userInfo && (
						<div className='navbar-mobile-actions ms-auto d-flex align-items-center'>
							<NotificationDropdown variant='mobile' />
							{renderMobileMenu()}
						</div>
					)
				) : (
					<>
						{showGlobalSearch ? (
							<Nav
								navbar
								className={classNames('navbar-search-wrapper align-items-center flex-grow-0', {
									/* 首页由 CSS 固定在 Logo 旁；其它页保留与 Logo 的间距 */
									'ms-2 ms-md-4': !isDashboardHome,
								})}
								as='ul'
								style={{ minWidth: 0 }}
							>
								<Nav.Item as='li' style={{ minWidth: 0 }}>
									<GlobalSearchBox showSeasonalIllustration={isDashboardHome} />
								</Nav.Item>
							</Nav>
						) : null}
						{showDesktopTopNav ? (
							<Nav navbar className='align-items-center d-none d-lg-flex flex-row flex-nowrap ms-2 flex-shrink-0'>
								<NavbarTopWorkMenus />
							</Nav>
						) : null}
						<Nav navbar className='navbar-nav-icons ms-auto flex-row align-items-center' as='ul'>
							{userInfo ? (
								<>
									<NavbarChartWorkbenchButton />
									<ThemeControlDropdown />
									<NotificationDropdown />
									<Dropdown navbar={true} as='li' className='d-flex align-items-center'>
										<Dropdown.Toggle bsPrefix='toggle' as={Link} to='#!' className='pe-0 ps-2 nav-link user-avatar-toggle' aria-label='用户菜单'>
											{renderUserAvatar()}
										</Dropdown.Toggle>
										<Dropdown.Menu className='dropdown-caret dropdown-menu-card dropdown-menu-end mt-2 user-dropdown-menu'>
											<div className='bg-white rounded-2 py-2 dark__bg-1000 shadow-lg border-0'>
												<div className='px-3 py-2 border-bottom mb-2'>
													<div className='d-flex align-items-center gap-2'>
														{renderUserAvatar(48, '1.25rem')}
														<div className='flex-grow-1 min-w-0'>
															<div className='fw-semibold fs-9 text-900 text-truncate' title={userInfo.name}>
																{userInfo.name}
															</div>
															<div className='text-muted fs-10 text-truncate' title={userInfo.email || userInfo.role}>
																{userInfo.email || userInfo.role}
															</div>
														</div>
													</div>
												</div>
												{renderAccountLinks()}
											</div>
										</Dropdown.Menu>
									</Dropdown>
								</>
							) : null}
						</Nav>
					</>
				)}
			</Navbar>
		</>
	);
};

export default Header;
